import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import type { RequisitionPrefill } from '../operations/requisitionPrefill';
import { ALERT_PAGE_CAP, fetchStockAlert, type StockAlertRow } from './icmAlertApi';
import '../../backlog/backlog.css';
import './icmAlerts.css';

/**
 * Inventory Management → Alert → Stock Alert.
 *
 * Items at or below their reorder level — "what must we order".  Ported from
 * ALPACA `Items_ListView_Copy_StockAlert`
 * (criteria `[StockQty] <= [AlertQty] And [StockQty] > 0`).
 *
 * Read-only apart from the one legacy action: `OrderActionId`, captioned
 * "Requisition", which opens the Item Requisition list with a draft pre-filled
 * from the item (vendor, manufacturer, unit price, catalog). Legacy
 * surfaces it TWICE from one declaration -- as a PER-ROW link (Category
 * "RecordEdit", which the ASPxGridListEditor renders as a command column ahead
 * of the data columns) and in the toolbar, where RequireSingleObject makes it
 * demand exactly one ticked row. Both are wired here.  The hand-off carries
 * that same data
 * via router `location.state` (see `RequisitionPrefill`) instead of legacy's
 * XAF session bag (`requisitionquerypanelinfo`, server-side per-user state —
 * §9 forbids that) — nothing is written to the database until the user
 * reviews the pre-filled New Item Requisition form and clicks Save.
 */
export default function StockAlertPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  // MAC rights (hide, never grey): the Requisition action opens a NEW requisition, so it needs
  // Write on this form AND Create on Item Requisition (what it actually does).
  const perm = useNavActions('icm-stock-alert');
  const reqPerm = useNavActions('icm-requisition');
  const canRaise = perm.write && reqPerm.create;

  const [rows, setRows] = useState<StockAlertRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchStockAlert();
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
      if (page.total > page.rows.length) {
        notify.info(`Showing the first ${ALERT_PAGE_CAP.toLocaleString()} of ${page.total.toLocaleString()} items — narrow the search to see the rest.`);
      }
    } catch {
      setError('Failed to load stock alerts.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.description ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.catalog ?? '').toLowerCase().includes(t) ||
      (r.storageNames ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  /**
   * Legacy `OrderAction` ("Requisition", `AlertViewControllercs.Designer.cs`
   * :36-41): `Category = "RecordEdit"` + `SelectionDependencyType.
   * RequireSingleObject`, targeted at `Items_ListView_Copy_StockAlert` only.
   * In XAF Web the RecordEdit container is rendered by the ASPxGridListEditor
   * as a PER-ROW command link, which is why GoLive shows "Requisition" as the
   * first column of every row -- it is an ACTION, not a model column (the
   * model's Columns node has no such entry, in either xafml).
   *
   * `OrderAction_Execute` then opens the requisition entry LIST
   * (`Frame.SetView(CreateListView("Requisition_ListViewEntermode", ...))`) and
   * `RequisitionViewcontroller` :305-343 adds an UNSAVED draft Requisition there
   * pre-filled from the item -- vendor, manufacturer, unit price, catalog, the
   * requester's department -- which the user edits in the grid and submits.
   *
   * So the target is the ITEM REQUISITION LIST, not a detail form: that is the
   * screen legacy opens, it is the screen that owns the draft grid and Submit,
   * and standing rule 10 requires a navigation to land on a list view. (AI
   * pointed this at `/m/icm-requisition/new` until 2026-10-06.) The payload
   * travels in router `location.state` rather than legacy's server-side XAF
   * session bag (`requisitionquerypanelinfo`, which §9 forbids); see
   * `../operations/requisitionPrefill`. Nothing is written until Submit.
   */
  function raiseRequisitionFor(row: StockAlertRow) {
    const prefill: RequisitionPrefill = {
      itemId: row.oid,
      itemName: row.itemName,
      itemCode: row.itemCode,
      description: row.description,
      vendorId: row.vendorId,
      vendorName: row.vendorName,
      manufacturerId: row.manufacturerId,
      manufacturerName: row.manufacturerName,
      catalog: row.catalog,
      unitPrice: row.unitPrice,
      stockQty: row.stockQty,
    };
    navigate('/m/icm-requisition', { state: { prefill } });
  }

  // Column set + order from the LIVE ALPACA model (BTLIMS.Web/Model.xafml:
  // Items_ListView_Copy_StockAlert) — Item · Description · Catalog# · Vendor ·
  // PackUnit · Storage · StockQty · AlertQty · ItemCode — behind the per-row
  // Requisition action link, which legacy renders ahead of them all.
  const cols: AppColumn<StockAlertRow>[] = [
    /* The action link. Header is blank because legacy's RecordEdit command
       column has none; `noExport` keeps it out of the spreadsheet, where a cell
       reading "Requisition" would look like a value rather than a button; and
       `stopPropagation` stops a row click firing as well as the link. */
    ...(canRaise ? [{
      field: 'requisition', header: '', width: 110, align: 'center',
      sortable: false, noExport: true, text: () => '',
      render: r => (
        <button
          type="button"
          className="adg-celllink bl-link"
          onClick={e => { e.stopPropagation(); raiseRequisitionFor(r); }}
          title={`Raise a requisition for ${r.itemName ?? 'this item'}`}
        >
          Requisition
        </button>
      ),
    } as AppColumn<StockAlertRow>] : []),
    { field: 'itemName',     header: 'Item',        width: 240, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'description',  header: 'Description', width: 260, text: r => r.description ?? '—', render: r => r.description ?? '—' },
    { field: 'catalog',      header: 'Catalog #',   width: 130, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'vendorName',   header: 'Vendor',      width: 150, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'packUnit',     header: 'Pack Unit',   width: 100, text: r => r.packUnit ?? '—', render: r => r.packUnit ?? '—' },
    { field: 'storageNames', header: 'Storage',     width: 150, text: r => r.storageNames ?? '—', render: r => r.storageNames ?? '—' },
    { field: 'stockQty',     header: 'Stock Qty',   width: 100, type: 'number', align: 'right', text: r => String(r.stockQty), value: r => r.stockQty },
    { field: 'alertQty',     header: 'Alert Qty',   width: 100, type: 'number', align: 'right', text: r => String(r.alertQty), value: r => r.alertQty },
    { field: 'itemCode',     header: 'Item Code',   width: 120, text: r => r.itemCode ?? '—', render: r => r.itemCode ?? '—' },
  ];

  /** The SAME action from the toolbar. Legacy's RequireSingleObject is what
   *  makes the toolbar copy demand exactly one selected row; the per-row link
   *  above needs no selection because the row IS the selection. */
  function raiseRequisition() {
    if (selected.size !== 1) {
      notify.warning('Select one item to raise a requisition for.');
      return;
    }
    const row = rows.find(r => r.oid === String(Array.from(selected)[0]));
    if (row) raiseRequisitionFor(row);
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Alert</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Stock Alert</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-stock-alert" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={canRaise ? (
              <button
                type="button"
                className="ca-btn"
                onClick={raiseRequisition}
                disabled={selected.size !== 1}
                title="Raise a requisition for the selected item"
              >
                <i className="ti ti-shopping-cart" /> Requisition
              </button>
            ) : undefined}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'stock-alert', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search item, code, description, vendor, storage…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} item(s) at or below reorder level</span>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<StockAlertRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              defaultPageSize={20}
              emptyText="No items are at or below their reorder level."
            />
          )}
        </div>
      </div>
    </div>
  );
}
